import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useAnimationControls, useDragControls } from "framer-motion";
import { DOCK_AT, timelineDelay } from "@/components/Reveal";
import { Search, Home, FolderGit2, BookOpen, Code, Sparkles, Mail, MessageSquare, Sun, Moon, X as CloseIcon, ArrowUpRight, Award, User } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CHAT_SOCIALS } from "@/components/SocialLinks";
import { Chat, CAL_COM_LINK, type ChatMessage } from "@/components/Chat";
import { Orbit } from "@/components/Hero";

// The menu lists these views (the other shortcut keys still work when it's closed)
const PHONE_VIEWS = ["Home", "About", "Experience", "Skills", "Projects", "Blogs", "Certifications", "Theme"];

export function Header() {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains("dark"));
  // Phone bottom sheet: drag the handle down to dismiss (desktop palette isn't draggable)
  const sheetDrag = useDragControls();
  const sheet = useAnimationControls();
  const isPhone = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
  // Open, snap-back and close are driven explicitly (an `animate` prop fights the drag and
  // leaves the sheet stuck after a short pull)
  const SHEET_SPRING = { type: "spring", damping: 32, stiffness: 340 } as const;
  const closeSheet = () => sheet.start({ y: "100%", transition: SHEET_SPRING }).then(() => setIsOpen(false));
  // Dock slides up last in the page-load sequence (after the hero, Experience and Skills).
  // Scheduled after mount so the hero exists to anchor the timeline.
  const [dockDelay, setDockDelay] = useState<number | null>(null);
  useEffect(() => setDockDelay(timelineDelay(DOCK_AT)), []);
  const toggleTheme = () => {
    const next = !document.documentElement.classList.contains("dark");
    document.documentElement.classList.toggle("dark", next);
    document.documentElement.classList.toggle("light", !next); // react-tweet reads .light/.dark
    try { localStorage.setItem("theme", next ? "dark" : "light"); } catch { /* storage blocked */ }
    setIsDark(next);
  };

  const [isOpen, setIsOpen] = useState(false);
  // C / Contact: cal.com booking in a pop-up instead of a new tab
  const [calOpen, setCalOpen] = useState(false);
  // Shortcut keys inside the open palette (H / P / B …): the row lights up with a short
  // progress bar and "Opening …" so you can see it's happening, then it navigates
  // Typing another letter while it's pending cancels it and keeps the text ("hi", "projects"),
  // so the box still works for search and chat.
  const [pendingShortcut, setPendingShortcut] = useState<string | null>(null);
  // Pill at the top ("[H] Home") whenever a shortcut runs, so it's obvious it worked,
  // even when you're already where it goes (e.g. H at the top of the home page)
  const [pill, setPill] = useState<{ key: string; label: string; id: number } | null>(null);
  const pillTimer = useRef<ReturnType<typeof setTimeout>>();
  const showPill = (key: string, label: string) => {
    setPill({ key, label, id: Date.now() });
    clearTimeout(pillTimer.current);
    pillTimer.current = setTimeout(() => setPill(null), 1500);
  };
  const pendingRef = useRef<{ key: string; timer: ReturnType<typeof setTimeout> } | null>(null);
  const runShortcut = (cmd: { label: string; shortcut?: string; action: () => void }, key = "") => {
    if (pendingRef.current) return;
    setPendingShortcut(cmd.label);
    pendingRef.current = {
      key,
      timer: setTimeout(() => {
        pendingRef.current = null;
        setPendingShortcut(null);
        if (cmd.shortcut) showPill(cmd.shortcut, cmd.label);
        cmd.action();
      }, 450),
    };
  };
  const cancelShortcut = () => {
    if (!pendingRef.current) return "";
    clearTimeout(pendingRef.current.timer);
    const key = pendingRef.current.key;
    pendingRef.current = null;
    setPendingShortcut(null);
    return key;
  };
  // Phone sheet slides up whenever it opens
  useEffect(() => {
    if (isOpen) sheet.start({ y: 0, transition: SHEET_SPRING });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const [searchQuery, setSearchQuery] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [forceChatMode, setForceChatMode] = useState(false);
  const [isVoiceCalling, setIsVoiceCalling] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Listen for voice call status
  useEffect(() => {
    const handleStatus = (e: any) => {
      setIsVoiceCalling(e.detail.isCalling);
    };
    window.addEventListener("voice-call-status", handleStatus);
    return () => window.removeEventListener("voice-call-status", handleStatus);
  }, []);

  const scrollToSection = (id: string) => {
    // 1. First, make sure menu is closed and scroll is unlocked
    setIsOpen(false);
    setSearchQuery("");
    
    // Unlock scroll synchronously so Lenis is active for the next steps
    document.body.style.overflow = '';
    // @ts-ignore
    if (window.lenis) window.lenis.start();

    // 2. Handle navigation and scrolling. Read the live URL: the keyboard-shortcut listener
    // keeps an old copy of this function, and its `location` would still say "/" after you
    // navigated to /blog or /projects in-app, so H silently did nothing there.
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          // @ts-ignore
          if (window.lenis) {
            // @ts-ignore
            window.lenis.scrollTo(element, { duration: 1.5 });
          } else {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }
      }, 300); // Increased timeout to ensure page load
    } else {
      const element = document.getElementById(id);
      if (element) {
        // @ts-ignore
        if (window.lenis) {
          // @ts-ignore
          window.lenis.scrollTo(element, { duration: 1.5 });
        } else {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  };

  useEffect(() => {
    const lockScroll = () => {
      document.body.style.overflow = 'hidden';
      // @ts-ignore
      if (window.lenis) window.lenis.stop();
    };

    const unlockScroll = () => {
      document.body.style.overflow = '';
      // @ts-ignore
      if (window.lenis) window.lenis.start();
    };

    if (isOpen) {
      lockScroll();
    } else {
      unlockScroll();
    }
    
    return () => {
      unlockScroll();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!calOpen) return;
    document.body.style.overflow = "hidden";
    // @ts-expect-error lenis is attached by SmoothScroll
    window.lenis?.stop();
    return () => {
      document.body.style.overflow = "";
      // @ts-expect-error lenis is attached by SmoothScroll
      window.lenis?.start();
    };
  }, [calOpen]);

  const socials = CHAT_SOCIALS.map((social) => ({ social, label: social.label, url: social.href, category: "SOCIALS" }));

  const commands = [
    {
      icon: MessageSquare,
      label: "Chat with Me",
      shortcut: "AI",
      action: () => { setForceChatMode(true); setSearchQuery(""); setIsOpen(true); },
      description: "Ask AI about Chirag",
      category: "FEATURED"
    },
    {
      icon: Home,
      label: "Home",
      shortcut: "H",
      action: () => scrollToSection("hero"),
      description: "Go to homepage",
      category: "VIEWS"
    },
    {
      icon: User,
      label: "About",
      shortcut: "O",
      action: () => { navigate("/about"); setIsOpen(false); setSearchQuery(""); },
      description: "Who I am",
      category: "VIEWS"
    },
    {
      icon: Code,
      label: "Experience",
      shortcut: "E",
      action: () => { navigate("/experience"); setIsOpen(false); setSearchQuery(""); },
      description: "View work history",
      category: "VIEWS"
    },
    {
      icon: Sparkles,
      label: "Skills",
      shortcut: "S",
      action: () => { navigate("/skills"); setIsOpen(false); setSearchQuery(""); },
      description: "Technical expertise",
      category: "VIEWS"
    },
    {
      icon: FolderGit2,
      label: "Projects",
      shortcut: "P",
      action: () => { navigate("/projects"); setIsOpen(false); setSearchQuery(""); },
      description: "Portfolio showcase",
      category: "VIEWS"
    },
    {
      icon: BookOpen,
      label: "Blogs",
      shortcut: "B",
      action: () => { navigate("/blogs"); setIsOpen(false); setSearchQuery(""); },
      description: "Read blogs",
      category: "VIEWS"
    },
    {
      icon: Award,
      label: "Certifications",
      shortcut: "A",
      action: () => { navigate("/certifications"); setIsOpen(false); setSearchQuery(""); },
      description: "Courses and awards",
      category: "VIEWS"
    },
    {
      icon: isDark ? Sun : Moon,
      label: "Theme",
      shortcut: "T",
      action: toggleTheme,
      description: isDark ? "Switch to light" : "Switch to dark",
      category: "VIEWS"
    },
    {
      icon: Mail,
      label: "Contact",
      shortcut: "C",
      action: () => { setIsOpen(false); setSearchQuery(""); setCalOpen(true); },
      description: "Book a call",
      category: "VIEWS"
    },
  ];

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isTyping = e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement;

      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(!isOpen);
        setForceChatMode(false);
        return;
      }

      if (e.key === '/' && !isTyping) {
        e.preventDefault();
        setIsOpen(true);
        setForceChatMode(false);
        return;
      }

      if (e.key === 'Escape') {
        setCalOpen(false);
        setIsOpen(false);
        setForceChatMode(false);
        return;
      }

      if (isTyping || e.ctrlKey || e.metaKey || e.altKey) return;
      const key = e.key.toUpperCase();
      if (isOpen) {
        // Menu open: H / E / P / B light up their row, then go
        if (forceChatMode) return;
        const cmd = commands.find((c) => c.shortcut === key && PHONE_VIEWS.includes(c.label));
        if (cmd) {
          e.preventDefault();
          runShortcut(cmd, e.key);
        }
        return;
      }
      const command = commands.find(cmd => cmd.shortcut === key);
      if (command) {
        e.preventDefault();
        showPill(key, command.label);
        command.action();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, forceChatMode]);



  const filteredCommands = commands.filter(cmd =>
    cmd.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cmd.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSocials = socials.filter(social =>
    social.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const featured = filteredCommands.filter(c => c.category === "FEATURED");
  const views = filteredCommands.filter(c => c.category === "VIEWS");

  // Only "Chat with Me" opens the chat now (there's no search box to type into)
  const showAIMode = forceChatMode;

  return (
    <>
      {/* Shortcut confirmation pill */}
      <AnimatePresence>
        {pill && (
          <motion.div
            key={pill.id}
            role="status"
            aria-live="polite"
            initial={{ opacity: 0, y: -12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 420, damping: 30 }}
            className="fixed inset-x-0 z-[90] flex justify-center pointer-events-none"
            style={{ top: "max(1rem, env(safe-area-inset-top))" }}
          >
            <div className="flex items-center gap-2.5 rounded-full border border-border bg-card/90 py-1.5 pl-1.5 pr-4 shadow-lg backdrop-blur-xl">
              <kbd className="flex h-7 w-7 items-center justify-center rounded-full border border-border bg-accent font-mono text-xs font-semibold text-foreground">
                {pill.key}
              </kbd>
              <span className="text-sm font-medium text-muted-foreground">{pill.label}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Soft blur behind the dock so scrolling content fades out under it. It hangs 6rem below
          the viewport: in-app browsers (X, Instagram) put the fixed bottom above their own toolbar
          and draw the page underneath it, so a strip that stopped at bottom-0 left a hard line
          with unblurred content below. Off-screen in normal browsers. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 z-[59] bg-gradient-to-t from-background/80 via-background/50 to-transparent backdrop-blur-[6px]"
        style={{
          bottom: "-6rem",
          height: "12rem",
          maskImage: "linear-gradient(to top, black 65%, transparent)",
          WebkitMaskImage: "linear-gradient(to top, black 65%, transparent)",
        }}
      />

      {/* Bottom Floating Navigation - Simple Pill */}
      <div 
        className="fixed left-0 right-0 z-[60] flex justify-center px-4"
        style={{ bottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
      >
        <TooltipProvider>
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={dockDelay === null ? undefined : { opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, delay: dockDelay ?? 0, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-1 bg-card/90 backdrop-blur-2xl border border-foreground/15 rounded-full p-1 shadow-[0_8px_30px_rgba(0,0,0,0.35)]"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => scrollToSection("hero")}
                  className="p-2.5 rounded-full md:hover:bg-foreground/10 transition-all duration-300 group active:scale-95"
                  aria-label="Home"
                >
                  <Home className="w-[18px] h-[18px] text-muted-foreground group-hover:text-foreground transition-colors" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-popover border-border text-popover-foreground">
                <p>Home</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => navigate("/projects")}
                  className="p-2.5 rounded-full md:hover:bg-foreground/10 transition-all duration-300 group active:scale-95"
                  aria-label="Projects"
                >
                  <FolderGit2 className="w-[18px] h-[18px] text-muted-foreground group-hover:text-foreground transition-colors" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-popover border-border text-popover-foreground">
                <p>Projects</p>
              </TooltipContent>
            </Tooltip>


            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => navigate("/blogs")}
                  className="p-2.5 rounded-full md:hover:bg-foreground/10 transition-all duration-300 group active:scale-95"
                  aria-label="Blogs"
                >
                  <BookOpen className="w-[18px] h-[18px] text-muted-foreground group-hover:text-foreground transition-colors" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-popover border-border text-popover-foreground">
                <p>Blogs</p>
              </TooltipContent>
            </Tooltip>

            <div className="w-px h-5 bg-foreground/10 mx-1" />
            
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className={`p-2.5 rounded-full md:hover:bg-foreground/10 transition-colors duration-300 group active:scale-95 ${
                    isOpen ? "bg-foreground/10" : ""
                  }`}
                  aria-label="Search and Chat"
                >
                  <Search className="w-[18px] h-[18px] text-muted-foreground group-hover:text-foreground transition-colors" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-popover border-border text-popover-foreground">
                <p>Search & Chat (Ctrl+K)</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleTheme}
                  className="p-2.5 rounded-full md:hover:bg-foreground/10 transition-colors duration-300 group active:scale-95"
                  aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
                >
                  <motion.span
                    key={isDark ? "moon" : "sun"}
                    initial={{ rotate: -90, opacity: 0, scale: 0.6 }}
                    animate={{ rotate: 0, opacity: 1, scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    className="block"
                  >
                    {isDark
                      ? <Sun className="w-[18px] h-[18px] text-muted-foreground group-hover:text-foreground transition-colors" />
                      : <Moon className="w-[18px] h-[18px] text-muted-foreground group-hover:text-foreground transition-colors" />}
                  </motion.span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-popover border-border text-popover-foreground">
                <p>{isDark ? "Light theme" : "Dark theme"}</p>
              </TooltipContent>
            </Tooltip>
          </motion.div>
        </TooltipProvider>
      </div>

      {/* Booking pop-up (C / Contact) */}
      <AnimatePresence>
        {calOpen && (
          <motion.div
            key="cal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-3 md:p-6"
            data-lenis-prevent
          >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setCalOpen(false)} />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label="Book a call with Chirag"
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 380, damping: 32 }}
              className="relative flex h-[min(720px,88dvh)] w-full max-w-[960px] flex-col overflow-hidden rounded-2xl border border-border bg-popover shadow-2xl"
            >
              <div className="flex items-center gap-3 border-b border-border px-4 py-3">
                <div className="relative shrink-0">
                  <img src="/profile.webp" alt="" width={36} height={36} className="h-9 w-9 rounded-full object-cover" />
                  <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-popover bg-emerald-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold leading-tight text-foreground">Book a call</p>
                  <p className="text-[12px] text-muted-foreground">30 min · Google Meet</p>
                </div>
                <a
                  href={CAL_COM_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open in a new tab"
                  className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <ArrowUpRight className="h-4 w-4" />
                </a>
                <button
                  onClick={() => setCalOpen(false)}
                  aria-label="Close"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <CloseIcon className="h-4 w-4" />
                </button>
              </div>
              <div className="shimmer relative flex-1">
                <iframe
                  src={`${CAL_COM_LINK}?embed=true&theme=${isDark ? "dark" : "light"}&layout=month_view`}
                  title="Book a call with Chirag"
                  className="relative z-[1] h-full w-full"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Command Menu & Chat - EXACTLY like Reference Image */}
      {isOpen && (
        <>
          <div
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[55] animate-in fade-in duration-300"
          />

          {/* Phones: bottom sheet. Desktop: centred palette */}
          <div className="fixed z-[60] inset-x-0 bottom-0 md:inset-x-auto md:bottom-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[640px] md:animate-in md:fade-in md:zoom-in-95 md:duration-200">
            <motion.div
              drag={isPhone ? "y" : false}
              dragListener={false}
              dragControls={sheetDrag}
              dragConstraints={{ top: 0 }}
              dragElastic={{ top: 0.05, bottom: 0 }}
              dragMomentum={false}
              onDragEnd={(_, info) => {
                if (info.offset.y > 120 || info.velocity.y > 600) closeSheet();
                else sheet.start({ y: 0, transition: SHEET_SPRING });
              }}
              initial={isPhone ? { y: "100%" } : false}
              animate={sheet}
              className="bg-popover border border-border rounded-t-2xl md:rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[85dvh] md:max-h-[80vh] pb-[env(safe-area-inset-bottom)] md:pb-0"
            >
              {/* Grab handle (phones): drag it down to close */}
              <div
                onPointerDown={(e) => sheetDrag.start(e)}
                className="md:hidden flex justify-center pt-2.5 pb-1.5 touch-none cursor-grab active:cursor-grabbing"
                aria-hidden="true"
              >
                <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
              </div>

              {showAIMode ? (
                <Chat
                  messages={messages}
                  setMessages={setMessages}
                  query={searchQuery}
                  setQuery={(v) => { setForceChatMode(true); setSearchQuery(v); }}
                  onBack={() => { setForceChatMode(false); setSearchQuery(""); }}
                  onNavigate={() => { setIsOpen(false); setForceChatMode(false); }}
                  isPhone={isPhone}
                />
              ) : null}

              {/* Content Area */}
              <div 
                className={`${showAIMode ? 'hidden' : 'flex-1 overflow-y-auto'} custom-scrollbar bg-popover`}
                data-lenis-prevent
              >
                {!showAIMode ? (
                  <div className="py-2">
                    
                    {/* FEATURED Section */}
                    {featured.length > 0 && (
                      <div className="mb-2">
                        <div className="px-4 py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                          FEATURED
                        </div>
                        <div className="px-2">
                          {featured.map((cmd, index) => {
                             const Icon = cmd.icon;
                             return (
                              <button
                                key={index}
                                onClick={cmd.action}
                                className="w-full flex items-center justify-between px-3 py-2.5 md:py-2 rounded-lg hover:bg-accent group transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <img src="/profile.webp" alt="" width={32} height={32} className="w-8 h-8 rounded-full object-cover" />
                                  <span className="text-[14px] font-medium text-foreground">{cmd.label}</span>
                                </div>
                                {/* No shortcut usually for featured items in ref image, but can add if needed */}
                              </button>
                             );
                          })}
                        </div>
                      </div>
                    )}

                    {/* VIEWS Section */}
                    {views.length > 0 && (
                      <div className="mb-2">
                         <div className="px-4 py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                          VIEWS
                        </div>
                        <div className="px-2">
                          {views.map((cmd, index) => {
                             const Icon = cmd.icon;
                             return (
                              <button
                                key={index}
                                onClick={() => runShortcut(cmd)}
                                className={`relative overflow-hidden w-full items-center justify-between px-3 py-2.5 md:py-2 rounded-lg hover:bg-accent group transition-colors ${
                                  PHONE_VIEWS.includes(cmd.label) ? "flex" : "hidden"
                                } ${pendingShortcut === cmd.label ? "bg-accent ring-1 ring-foreground/20" : ""}`}
                              >
                                {pendingShortcut === cmd.label && (
                                  <motion.span
                                    className="absolute bottom-0 left-0 h-0.5 bg-foreground/70"
                                    initial={{ width: "0%" }}
                                    animate={{ width: "100%" }}
                                    transition={{ duration: 0.45, ease: "easeInOut" }}
                                  />
                                )}
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center justify-center w-8 h-8 rounded-md bg-accent border border-border text-muted-foreground group-hover:text-foreground transition-colors">
                                    <Icon className="w-4 h-4" />
                                  </div>
                                  <span className="text-[14px] font-medium text-foreground">
                                    {cmd.label === "Theme"
                                      ? (pendingShortcut === cmd.label ? "Switching theme…" : isDark ? "Light mode" : "Dark mode")
                                      : pendingShortcut === cmd.label ? `Opening ${cmd.label}…` : cmd.label}
                                  </span>
                                </div>
                                <kbd
                                  className={`flex items-center justify-center w-6 h-6 rounded border font-mono text-[10px] transition-colors ${
                                    pendingShortcut === cmd.label
                                      ? "bg-foreground text-background border-foreground"
                                      : "bg-accent border-border text-muted-foreground"
                                  }`}
                                >
                                  {cmd.shortcut}
                                </kbd>
                              </button>
                             );
                          })}
                        </div>
                      </div>
                    )}

                    {/* SOCIALS Section */}
                    {filteredSocials.length > 0 && (
                       <div className="mb-2">
                         <div className="px-4 py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                          SOCIALS
                        </div>
                        {/* Same photo + orbiting ring as the profile popup */}
                        <div className="flex justify-center pb-3">
                          <Orbit socials={filteredSocials.map(({ social }) => social)} />
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              {/* Footer */}
              <div className={`${showAIMode ? "hidden" : "hidden md:flex"} px-4 py-3 bg-popover border-t border-border items-center justify-between text-[11px] text-muted-foreground`}>
                <div className="font-medium">Actions</div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5">
                    <span>Open</span>
                    <kbd className="px-1.5 py-0.5 rounded bg-accent border border-border font-mono text-[10px] text-muted-foreground">↵</kbd>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span>Close</span>
                    <kbd className="px-1.5 py-0.5 rounded bg-accent border border-border font-mono text-[10px] text-muted-foreground">Esc</kbd>
                  </div>
                </div>
              </div>
              
            </motion.div>
          </div>
        </>
      )}
    </>
  );
}
